<template>
    <div @click="viewProject" v-if="project"
         class="home-project-card group relative flex min-h-[120px] cursor-pointer items-center gap-x-4 rounded-lg border border-transparent bg-white p-4 pr-11 hover:border-eureka-500"
         :class="{ '!min-h-[256px] !gap-x-12 !p-12': size === 'lg' }">

        <ProjectIcon class="shrink-0" :icon="project.icon" :size="size" />

        <div class="flex h-full grow flex-col justify-between border-l border-gray-40 pl-4"
             :class="{ '!pl-12': size === 'lg' }">
            <!--  上 -->
            <div class="grow">
                <p class="line-clamp-1 text-sm font-semibold text-black"
                   :class="{ '!line-clamp-2 !text-lg !leading-6': size === 'lg' }" :title="project.project_title">
                    {{ project.project_title }}
                </p>

                <div class="mt-2" v-if="size === 'lg'">
                    <div class="th-badge inline-block h-7 max-w-[45%] truncate leading-7 first:mr-2" :title="item"
                         v-for="(item, index) in project.tag" :key="index">
                        {{ item }}
                    </div>
                </div>
            </div>

            <!-- 下 -->
            <div class="flex flex-col gap-1 text-xs text-grey-90">
                <p>
                    {{ projectUpdateTime }}
                </p>
            </div>

        </div>

        <div v-if="project.pinned" class="absolute left-4 top-4">
            <div class="th-tooltip th-tooltip-dark" :data-tip="t('cancelPin')">
                <button class="th-btn-only-icon th-btn th-btn-plain th-btn-sm" @click.stop="cancelPin">
                    <PtIcon icon="PinnedIcon" class="text-2xl"/>
                </button>
            </div>
        </div>

        <CardMenu class="absolute right-4 top-4" :project="project" @remove-project="handleRemoveProject"
                  @pin-project="handlePinProject" />

        <button class="th-btn th-btn-sm invisible absolute bottom-4 right-4 group-hover:visible"
                :class="{ '!th-btn-md !visible !bottom-12 !right-12': size === 'lg'}"
        >
            <span> {{t('viewProject')}} </span>
            <PtIcon class="th-btn-icon" icon="RightArrow" v-if="size === 'lg'"/>
        </button>
    </div>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz'
import CardMenu from './CardMenu.vue'
import ProjectIcon from '@/components/ProjectIcon.vue'
import type { IProject, TSize } from '@/types'
import { useRouter } from 'vue-router'
import { computed } from 'vue'
import { formatDate } from '@eureka/utils/date-format'
import { useI18n } from 'vue-i18n'
import { trackViewProjectAPI } from '@/infrastructure/api-functions'
import '@/assets/icons/rightArrow.svg'
import '@/assets/icons/pinnedIcon.svg'

const { t } = useI18n()

const props = withDefaults(defineProps<{
    project: IProject
    size?: TSize
}>(), {
    size: 'md',
})

const router = useRouter()

function trackProjectView() {
    try {
        trackViewProjectAPI(props.project.project_id);
    } catch (error) {
    }
}

function viewProject() {
    router.push(`report/${props.project.project_id}`);
    trackProjectView()
}

const { locale } = useI18n()
const projectUpdateTime = computed(() => {
    return formatDate({ date: props.project.data_updated_at, currentLang: locale.value })
})

const emits = defineEmits<{
    (e: 'remove-project', id: string): void
    (e: 'pin-project', id: string): void
    (e: 'cancel-pin-project', id: string): void
}>()

function handleRemoveProject(id: string) {
    emits('remove-project', id)
}

function handlePinProject(id: string) {
    emits('pin-project', id)
}

function cancelPin() {
    emits('cancel-pin-project', props.project.project_id)
}
</script>

<style lang="scss">
.home-project-card {
    &:hover {
        box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.08), 0px 2px 6px 0px rgba(0, 0, 0, 0.08);
    }
}
</style>
